import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 思维启发与个人成长 - 竖屏「成长之树」风格
 *
 * 核心亮点：
 * - SVG 树干从根部向上生长动画
 * - 分支逐条展开
 * - 果实节点弹入（代表成长果实）
 * - 根系代表基础/底层能力
 * - 树叶粒子飘落效果
 * - 地面纹理 + 天空渐变
 *
 * 尺寸：1080x1920 竖屏
 */

interface TreeFruit {
  label: string;
  icon: string;
  color: string;
}

interface TreeRoot {
  label: string;
}

interface MindsetGrowthTreeProps {
  title: string;
  subtitle: string;
  fruits: TreeFruit[];
  roots: TreeRoot[];
  trunkLabel: string;
  insight: string;
}

const DEFAULT_PROPS: MindsetGrowthTreeProps = {
  title: '个人成长之树',
  subtitle: '扎深根基，才能结出硕果',
  fruits: [
    { label: '领导力', icon: '👑', color: '#fbbf24' },
    { label: '创造力', icon: '🎨', color: '#f472b6' },
    { label: '影响力', icon: '🌟', color: '#818cf8' },
    { label: '财富', icon: '💎', color: '#34d399' },
    { label: '幸福感', icon: '☀️', color: '#fb923c' },
  ],
  roots: [
    { label: '自律' },
    { label: '好奇心' },
    { label: '韧性' },
    { label: '同理心' },
  ],
  trunkLabel: '持续学习',
  insight: '看不见的根，决定了看得见的果',
};

const FallingLeaf: React.FC<{ delay: number; startX: number }> = ({ delay, startX }) => {
  const frame = useCurrentFrame();
  const t = ((frame - delay + 200) % 200) / 200;
  if (frame < delay) return null;

  const y = interpolate(t, [0, 1], [400, 1100]);
  const x = startX + Math.sin(t * 6 + delay) * 40;
  const rotate = t * 360;
  const opacity = interpolate(t, [0, 0.1, 0.8, 1], [0, 0.5, 0.5, 0]);

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: 10,
        height: 10,
        borderRadius: '50% 0 50% 50%',
        background: '#34d39960',
        transform: `rotate(${rotate}deg)`,
        opacity,
      }}
    />
  );
};

export const MindsetGrowthTree: React.FC<Partial<MindsetGrowthTreeProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Title (0-40)
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  // Tree growth phases
  const trunkGrow = interpolate(frame, [40, 140], [0, 1], { extrapolateRight: 'clamp' });
  const branchGrow = interpolate(frame, [100, 200], [0, 1], { extrapolateRight: 'clamp' });
  const rootGrow = interpolate(frame, [60, 130], [0, 1], { extrapolateRight: 'clamp' });

  // Fruits
  const fruitStart = 180;
  const fruitInterval = 35;

  // Roots labels
  const rootLabelStart = 130;

  // Trunk label
  const trunkLabelOpacity = interpolate(frame, [140, 165], [0, 1], { extrapolateRight: 'clamp' });

  // Insight
  const insightStart = fruitStart + p.fruits.length * fruitInterval + 30;
  const insightOpacity = interpolate(frame, [insightStart, insightStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Tree geometry
  const treeCx = 540;
  const groundY = 1380;
  const trunkTopY = 720;
  const trunkHeight = (groundY - trunkTopY) * trunkGrow;

  // Fruit positions (arc around canopy)
  const fruitPositions = p.fruits.map((_, i) => {
    const angle = -Math.PI / 2 + ((i - (p.fruits.length - 1) / 2) * Math.PI * 1.2) / Math.max(p.fruits.length, 1);
    const r = 260 + (i % 2) * 60;
    return {
      x: treeCx + r * Math.cos(angle),
      y: trunkTopY - 160 + r * Math.sin(angle) * 0.65,
    };
  });

  // Root positions
  const rootPositions = p.roots.map((_, i) => {
    const spread = ((i - (p.roots.length - 1) / 2) * 200) / p.roots.length;
    return {
      x: treeCx + spread * 2.5,
      y: groundY + 130 + (i % 2) * 40 + Math.abs(spread) * 0.3,
    };
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #fef3c7 0%, #fef9c3 20%, #ecfccb 40%, #d9f99d 80%, #a3e635 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Serif SC", serif',
        color: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* Sky gradient top */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 600,
          background: 'linear-gradient(180deg, #dbeafe 0%, #fef3c7 100%)',
        }}
      />

      {/* Falling leaves */}
      {Array.from({ length: 8 }).map((_, i) => (
        <FallingLeaf key={i} delay={180 + i * 25} startX={300 + (i * 67) % 480} />
      ))}

      {/* Ground */}
      <div
        style={{
          position: 'absolute',
          top: groundY - 20,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, #854d0e20, #78350f30)',
          borderRadius: '50% 50% 0 0',
        }}
      />

      {/* ========== SVG TREE ========== */}
      <svg style={{ position: 'absolute', inset: 0 }} width="1080" height="1920" viewBox="0 0 1080 1920">
        {/* Roots */}
        {rootPositions.map((rp, i) => {
          const dx = rp.x - treeCx;
          const cp1x = treeCx + dx * 0.3;
          const cp1y = groundY + 20;
          const cp2x = treeCx + dx * 0.7;
          const cp2y = rp.y - 20;
          const d = `M${treeCx},${groundY} C${cp1x},${cp1y} ${cp2x},${cp2y} ${rp.x},${rp.y}`;
          return (
            <path
              key={`root-${i}`}
              d={d}
              fill="none"
              stroke="#78350f"
              strokeWidth={10 - i * 0.8}
              strokeLinecap="round"
              strokeDasharray="400"
              strokeDashoffset={400 * (1 - rootGrow)}
              opacity="0.5"
            />
          );
        })}

        {/* Trunk */}
        <line
          x1={treeCx}
          y1={groundY}
          x2={treeCx}
          y2={groundY - trunkHeight}
          stroke="#78350f"
          strokeWidth="28"
          strokeLinecap="round"
          opacity="0.7"
        />
        {/* Trunk highlight */}
        <line
          x1={treeCx + 3}
          y1={groundY}
          x2={treeCx + 3}
          y2={groundY - trunkHeight}
          stroke="#92400e"
          strokeWidth="8"
          strokeLinecap="round"
          opacity="0.3"
        />

        {/* Branches to fruits */}
        {fruitPositions.map((fp, i) => {
          const d = `M${treeCx},${trunkTopY + 50} Q${treeCx + (fp.x - treeCx) * 0.3},${trunkTopY} ${fp.x},${fp.y + 30}`;
          return (
            <path
              key={`branch-${i}`}
              d={d}
              fill="none"
              stroke="#78350f"
              strokeWidth={8 - i * 0.5}
              strokeLinecap="round"
              strokeDasharray="500"
              strokeDashoffset={500 * (1 - branchGrow)}
              opacity="0.4"
            />
          );
        })}

        {/* Canopy glow */}
        <ellipse
          cx={treeCx}
          cy={trunkTopY - 100}
          rx={380 * branchGrow}
          ry={240 * branchGrow}
          fill="#22c55e"
          opacity="0.08"
        />
        <ellipse
          cx={treeCx}
          cy={trunkTopY - 100}
          rx={300 * branchGrow}
          ry={190 * branchGrow}
          fill="#16a34a"
          opacity="0.06"
        />
      </svg>

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -15}px)`,
        }}
      >
        <h1 style={{ fontSize: 70, fontWeight: 800, margin: 0, color: '#1c1917' }}>{p.title}</h1>
        <p style={{ fontSize: 40, color: '#78716c', marginTop: 10 }}>{p.subtitle}</p>
      </div>

      {/* Trunk label */}
      <div
        style={{
          position: 'absolute',
          left: treeCx + 30,
          top: (groundY + trunkTopY) / 2 - 20,
          opacity: trunkLabelOpacity,
        }}
      >
        <div
          style={{
            padding: '8px 20px',
            background: 'rgba(120,53,15,0.1)',
            borderRadius: 10,
            border: '1px solid rgba(120,53,15,0.2)',
            fontSize: 38,
            fontWeight: 800,
            color: '#78350f',
            writingMode: 'vertical-rl',
          }}
        >
          🌱 {p.trunkLabel}
        </div>
      </div>

      {/* Fruit nodes */}
      {fruitPositions.map((fp, i) => {
        const start = fruitStart + i * fruitInterval;
        const fruitScale = spring({
          frame: frame - start,
          fps,
          from: 0,
          to: 1,
          config: { damping: 8, stiffness: 150, mass: 0.5 },
        });
        const fruit = p.fruits[i];
        const bounce = interpolate(Math.sin((frame - start) / 12 + i), [-1, 1], [-3, 3]);

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: fp.x,
              top: fp.y + bounce,
              transform: `translate(-50%, -50%) scale(${fruitScale})`,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 22,
                background: 'white',
                border: `3px solid ${fruit.color}50`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 40,
                boxShadow: `0 4px 20px ${fruit.color}30`,
                margin: '0 auto',
              }}
            >
              {fruit.icon}
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color: '#1c1917', marginTop: 8 }}>{fruit.label}</div>
          </div>
        );
      })}

      {/* Root labels */}
      {rootPositions.map((rp, i) => {
        const opacity = interpolate(frame, [rootLabelStart + i * 20, rootLabelStart + i * 20 + 20], [0, 1], { extrapolateRight: 'clamp' });
        return (
          <div
            key={`rl-${i}`}
            style={{
              position: 'absolute',
              left: rp.x,
              top: rp.y + 10,
              transform: 'translate(-50%, 0)',
              opacity,
            }}
          >
            <div
              style={{
                padding: '6px 16px',
                background: 'rgba(120,53,15,0.08)',
                borderRadius: 8,
                fontSize: 38,
                color: '#78350f',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              🌿 {p.roots[i].label}
            </div>
          </div>
        );
      })}

      {/* Insight */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: insightOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '18px 36px',
            background: 'rgba(255,255,255,0.85)',
            borderRadius: 20,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            fontSize: 40,
            fontWeight: 600,
            color: '#1c1917',
          }}
        >
          💡 {p.insight}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, insightStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #78350f, #16a34a)',
        }}
      />
    </AbsoluteFill>
  );
};
